import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import ImageUpload from "@/components/admin/ImageUpload";
import { useSettingsEditor, SaveBar } from "@/components/admin/useSettingsEditor";

function StringListEditor({ label, items, onChange }) {
  const set = (i, v) => { const next = [...items]; next[i] = v; onChange(next); };
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</p>
        <Button variant="outline" size="sm" onClick={() => onChange([...items, ""])} className="rounded-full h-9"><Plus className="w-4 h-4 mr-1.5" />Add</Button>
      </div>
      <div className="space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex gap-2">
            <Input value={v} onChange={(e) => set(i, e.target.value)} className="h-10 rounded-lg border-slate-200" />
            <button className="text-rose-500 p-2" onClick={() => onChange(items.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminCourses() {
  const { draft, patchPath, save, reset, saving } = useSettingsEditor();
  if (!draft) return null;

  const c = draft.courses || {};
  const items = c.items || [];
  const other = c.otherPrograms || [];
  const about = draft.about || {};

  const setCourse = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    patchPath("courses.items", next);
  };

  const setCourseSpec = (i, specs) => {
    const next = [...items];
    next[i] = { ...next[i], specializations: specs };
    patchPath("courses.items", next);
  };

  const addCourse = () =>
    patchPath("courses.items", [
      ...items,
      { slug: `course-${items.length + 1}`, title: "New Course", duration: "2 Years", tag: "New", desc: "Description...", specializations: [] },
    ]);

  const removeCourse = (i) => patchPath("courses.items", items.filter((_, idx) => idx !== i));

  return (
    <div data-testid="cms-courses-page">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-2">Academics</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-slate-900">Courses & About</h1>
        <p className="text-slate-500 mt-2">Manage programs offered and the About page content.</p>
      </div>

      <div className="space-y-6">
        {/* Courses section header */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm">Section Eyebrow</Label>
              <Input value={c.eyebrow || ""} onChange={(e) => patchPath("courses.eyebrow", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div><Label className="text-slate-700 text-sm">Section Heading</Label>
              <Input value={c.heading || ""} onChange={(e) => patchPath("courses.heading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Intro Text</Label>
              <Textarea value={c.intro || ""} onChange={(e) => patchPath("courses.intro", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[80px]" />
            </div>
          </div>
        </div>

        {/* Course cards */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Course Cards</p>
            <Button variant="outline" size="sm" onClick={addCourse} className="rounded-full h-9" data-testid="add-course">
              <Plus className="w-4 h-4 mr-1.5" /> Add Course
            </Button>
          </div>
          <div className="space-y-5">
            {items.map((it, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-4 sm:p-5 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <h4 className="font-heading text-lg text-slate-900">{it.title || "Untitled Course"}</h4>
                  <button className="text-rose-500 p-2" onClick={() => removeCourse(i)}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <div className="sm:col-span-2"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Title</Label>
                    <Input value={it.title || ""} onChange={(e) => setCourse(i, "title", e.target.value)} className="mt-1 h-10 rounded-lg border-slate-200" />
                  </div>
                  <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Duration</Label>
                    <Input value={it.duration || ""} onChange={(e) => setCourse(i, "duration", e.target.value)} className="mt-1 h-10 rounded-lg border-slate-200" />
                  </div>
                  <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Tag</Label>
                    <Input value={it.tag || ""} onChange={(e) => setCourse(i, "tag", e.target.value)} className="mt-1 h-10 rounded-lg border-slate-200" />
                  </div>
                  <div className="sm:col-span-4"><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Description</Label>
                    <Textarea value={it.desc || ""} onChange={(e) => setCourse(i, "desc", e.target.value)} className="mt-1 rounded-lg border-slate-200 min-h-[80px]" />
                  </div>
                  <div className="sm:col-span-4">
                    <StringListEditor
                      label="Specializations (optional)"
                      items={it.specializations || []}
                      onChange={(v) => setCourseSpec(i, v)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8">
          <StringListEditor label="Other Programs" items={other} onChange={(v) => patchPath("courses.otherPrograms", v)} />
        </div>

        {/* About */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">About Page</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm">Eyebrow</Label>
              <Input value={about.eyebrow || ""} onChange={(e) => patchPath("about.eyebrow", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div><Label className="text-slate-700 text-sm">Heading</Label>
              <Input value={about.heading || ""} onChange={(e) => patchPath("about.heading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Paragraph 1</Label>
              <Textarea value={about.paragraph1 || ""} onChange={(e) => patchPath("about.paragraph1", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[100px]" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Paragraph 2</Label>
              <Textarea value={about.paragraph2 || ""} onChange={(e) => patchPath("about.paragraph2", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[100px]" />
            </div>
            <div className="sm:col-span-2">
              <ImageUpload label="Header Image" value={about.image} onChange={(url) => patchPath("about.image", url)} folder="images" testid="about-image" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Vision</Label>
              <Textarea value={about.vision || ""} onChange={(e) => patchPath("about.vision", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[80px]" />
            </div>
            <div className="sm:col-span-2">
              <StringListEditor label="Mission Points" items={about.mission || []} onChange={(v) => patchPath("about.mission", v)} />
            </div>
          </div>

          {["chairman", "principal"].map((role) => {
            const p = about[role] || {};
            return (
              <div key={role} className="border-t border-slate-100 pt-5">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">{role[0].toUpperCase() + role.slice(1)}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-700 text-sm">Name</Label>
                    <Input value={p.name || ""} onChange={(e) => patchPath(`about.${role}.name`, e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
                  </div>
                  <div><Label className="text-slate-700 text-sm">Title</Label>
                    <Input value={p.title || ""} onChange={(e) => patchPath(`about.${role}.title`, e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
                  </div>
                  <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Message</Label>
                    <Textarea value={p.message || ""} onChange={(e) => patchPath(`about.${role}.message`, e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[140px]" />
                  </div>
                  <div className="sm:col-span-2">
                    <ImageUpload label="Photo (optional)" value={p.photo} onChange={(url) => patchPath(`about.${role}.photo`, url)} folder="images" testid={`${role}-photo`} height={140} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <SaveBar onSave={save} onReset={reset} saving={saving} />
    </div>
  );
}
